import { useCallback } from 'react';
import Cookies from 'js-cookie';
import { baseUrl } from '../baseUrl';
import { useNavigate } from 'react-router-dom';
import { useAppProvider } from '../applicationProvider';

const useFetchWithAuth = () => {
  const navigate = useNavigate();
  const getAccessToken = (): string | undefined => Cookies.get('accessToken');
  const getRefreshToken = (): string | undefined => Cookies.get('refreshToken');
  const { setLoggedIn } = useAppProvider();
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const refresh_token = getRefreshToken();
      if (!refresh_token) throw new Error('No refresh token');

      const response = await fetch(`${baseUrl}refresh-token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });

      if (!response.ok) throw new Error('Failed to refresh token');

      const data = await response.json();
      Cookies.set('accessToken', data.access_token, {
        secure: true,
        sameSite: 'Strict',
      });
      Cookies.set('accessToken', data.access_token);
      return data.access_token;
    } catch (error) {
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      Cookies.remove('loggedin');
      Object.keys(Cookies.get()).forEach((cookie) => Cookies.remove(cookie));
      sessionStorage.clear();
      setLoggedIn(false);
      navigate('/');
      return null;
    }
    // eslint-disable-next-line
  }, [navigate]);

  const fetchWithAuth = useCallback(
    // eslint-disable-next-line
    async (url: string, options: RequestInit = {}, customBaseUrl?: string): Promise<{ data: any; error: string | null }> => {
      // eslint-disable-next-line
      const makeRequest = async (retryAttempted = false): Promise<{ data: any; error: string | null }> => {
        const accessToken = getAccessToken();

        const requestOptions: RequestInit = {
          method: options.method || 'GET',
          headers: new Headers(options.headers || {}),
          ...options,
        };
        if (!requestOptions.headers) {
          requestOptions.headers = new Headers();
        }
        if (!(requestOptions.headers instanceof Headers)) {
          requestOptions.headers = new Headers(requestOptions.headers);
        }

        // Only set Content-Type to application/json if the body is not FormData
        if (!(options.body instanceof FormData)) {
          requestOptions.headers.set('Content-Type', 'application/json');
        }

        requestOptions.headers.set('Authorization', `Bearer ${accessToken || ''} , Api-Key SHlsL0VF.sKpIYta4BY3x0BDN2UrQC5wZUFD1PIZm`);

        try {
          const response = await fetch(`${customBaseUrl ? customBaseUrl : baseUrl}${url}`, requestOptions);
          if (response.status === 401 && !retryAttempted) {
            const newAccessToken = await refreshAccessToken();
            if (newAccessToken) {
              requestOptions.headers.set('Authorization', `Bearer ${newAccessToken}`);
              return makeRequest(true);
            }
          }
          const responseBody = await response.text();
          const data = responseBody ? JSON.parse(responseBody) : null;
          if (!response.ok) {
            return {
              data: null,
              error: data?.error || data?.message || `HTTP error! Status: ${response.status}`,
            };
          }
          return { data, error: null };
        } catch (error) {
          return { data: null, error: (error as Error).message };
        }
      };
      return makeRequest();
    },
    [refreshAccessToken]
  );
  return { fetchWithAuth };
};

export default useFetchWithAuth;
